import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || '';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Intercept QBO_REAUTH_REQUIRED responses and redirect to Intuit immediately.
// We append the current page path to the OAuth state so the callback can
// redirect the user back to where they were instead of to /admin/settings.
axios.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && err.response?.data?.code === 'QBO_REAUTH_REQUIRED') {
      const authUrl = err.response.data.authUrl;
      if (authUrl) {
        try {
          const url = new URL(authUrl);
          const currentState = url.searchParams.get('state') || '';
          const returnTo = window.location.pathname + window.location.search;
          url.searchParams.set('state', `${currentState}|${encodeURIComponent(returnTo)}`);
          window.location.href = url.toString();
        } catch {
          window.location.href = authUrl;
        }
        return new Promise(() => {}); // never resolves — browser is navigating away
      }
    }
    return Promise.reject(err);
  }
);

const qboService = {
  getAuthUrl: () =>
    axios.get(`${BASE}/api/qbo/auth-url`, auth()).then(r => r.data.url),

  getStatus: () =>
    axios.get(`${BASE}/api/qbo/status`, auth()).then(r => r.data),

  disconnect: () =>
    axios.delete(`${BASE}/api/qbo/connection`, auth()).then(r => r.data),

  // Customer sync
  previewCustomers: () =>
    axios.get(`${BASE}/api/qbo/sync/customers/preview`, auth()).then(r => r.data),

  applyCustomers: (payload) =>
    axios.post(`${BASE}/api/qbo/sync/customers/apply`, payload, auth()).then(r => r.data),

  // Push a quickLOG-created customer to QuickBooks (creates it there if needed)
  pushCustomerToQbo: (customerId) =>
    axios.post(`${BASE}/api/qbo/customers/${customerId}/push`, {}, auth()).then(r => r.data),

  // Item sync
  previewItems: () =>
    axios.get(`${BASE}/api/qbo/sync/items/preview`, auth()).then(r => r.data),

  applyItems: (payload) =>
    axios.post(`${BASE}/api/qbo/sync/items/apply`, payload, auth()).then(r => r.data),

  // Ingredient → expense account sync (AI-matched — see docs/RECEIPT_SCAN_MODULE.md's Phase 2)
  previewIngredientAccounts: () =>
    axios.get(`${BASE}/api/qbo/sync/ingredient-accounts/preview`, { ...auth(), timeout: 30000 }).then(r => r.data),

  applyIngredientAccounts: (payload) =>
    axios.post(`${BASE}/api/qbo/sync/ingredient-accounts/apply`, payload, auth()).then(r => r.data),

  // All QBO item names (for dropdowns)
  getItems: () =>
    axios.get(`${BASE}/api/qbo/items`, auth()).then(r => r.data.items || []),

  // Lightweight: which productIds are linked to QBO
  getLinkedProducts: () =>
    axios.get(`${BASE}/api/qbo/sync/items/linked-products`, auth()).then(r => r.data),

  // Expense/COGS accounts by default; pass types=['Bank','Credit Card'] for the
  // "which account does this post against" picker.
  getAccounts: (types) =>
    axios.get(`${BASE}/api/qbo/accounts`, { ...auth(), params: types ? { types: types.join(',') } : {} }).then(r => r.data.accounts || []),

  // Invoice
  invoicePreflight: (orderId) =>
    axios.get(`${BASE}/api/qbo/invoices/preflight/${orderId}`, auth()).then(r => r.data),

  createInvoice: (orderId, { sendEmail = false } = {}) =>
    axios.post(`${BASE}/api/qbo/invoices`, { orderId, sendEmail }, auth()).then(r => r.data),

  sendInvoiceEmail: (orderId) =>
    axios.post(`${BASE}/api/qbo/invoices/${orderId}/send-email`, {}, auth()).then(r => r.data),
};

export default qboService;
